import { docMeta, YearFolders } from "@/components/year-folders";
import { MODULE_LABELS, type Module } from "@clientflow/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Download, Eye, FileText, Lock, MessageSquare, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";
import { Empty, Loading, PageHeader, Section, useServerPaged } from "@/components/page";
import { NoThreadSelected, ThreadList, ThreadView, UploadButton } from "@/components/panels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { api, ApiError, errorMessage } from "@/lib/api";
import { formatBytes, formatDate, formatPeriod } from "@/lib/format";
import type { Me } from "@/lib/session";
import type { Doc } from "@/lib/types";
import { useToast } from "@/lib/use-toast";
import { ask } from "@/components/dialogs";
import { DocumentPreviewDialog } from "@/components/document-viewer";
import { CONSENT_LABELS } from "@/components/tax-panels";
import { Invitations, MfaSetup } from "./auth";
import { BooksQuestionsCard } from "./portal-books";
import { PortalTodoCards, usePortalTodos } from "./portal-tax";
import { PortalPlanningCard } from "@/components/planning";

type PortalHome = {
  clients: Array<{ id: string; displayName: string; kind: string; access: string; services: Array<{ module: Module; level: string | null }>; hiddenDashboards?: string[] }>;
  actions: Array<{
    id: string;
    clientId: string;
    type: "document" | "question" | "approval";
    title: string;
    instructions: string | null;
    reviewNote: string | null;
    documentCount: number;
    dueDate: string | null;
  }>;
  work: Array<{ id: string; clientId: string; module: Module; name: string; period: string | null; statusLabel: string; stageCount: number; stageNumber: number }>;
  unreadThreads: number;
};

export function usePortalHome() {
  return useQuery<PortalHome>({ queryKey: ["/api/portal/home"], refetchInterval: 60_000 });
}

function ActionCard({ action, clientName }: { action: PortalHome["actions"][number]; clientName?: string }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [answer, setAnswer] = useState("");
  const [uploaded, setUploaded] = useState<string[]>([]);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["/api/portal/home"] });
    qc.invalidateQueries({ queryKey: ["/api/portal/documents"] });
  };
  const upload = useMutation({
    mutationFn: (file: File) => api.upload<{ filename: string }>("/api/portal/documents", { clientId: action.clientId, requestId: action.id, folder: "Uploads" }, file),
    onSuccess: (d) => setUploaded((u) => [...u, d.filename]),
    onError: (e) => toast({ title: "Upload failed", description: errorMessage(e), variant: "destructive" }),
  });
  // Several phone photos of one document (each page of a statement) can be sent as one PDF.
  const [photos, setPhotos] = useState<File[] | null>(null);
  const [asOne, setAsOne] = useState(true);
  const [docName, setDocName] = useState(action.title.replace(/\.(pdf|jpe?g|png|heic|heif)$/i, ""));
  const combine = useMutation({
    mutationFn: (files: File[]) => api.uploadFiles<{ filename: string }>("/api/portal/documents/combine", { clientId: action.clientId, requestId: action.id, folder: "Uploads", name: docName.trim() || action.title }, files),
    onSuccess: (d) => (setUploaded((u) => [...u, d.filename]), setPhotos(null)),
    onError: (e) => toast({ title: "Upload failed", description: errorMessage(e), variant: "destructive" }),
  });
  const pick = (files: File[]) => {
    if (files.length >= 2 && files.length <= 30 && files.every((f) => /\.(jpe?g|png)$/i.test(f.name))) {
      setPhotos(files);
      setAsOne(true);
      return;
    }
    for (const f of files) upload.mutate(f);
  };
  const sendPhotos = () => {
    if (!photos) return;
    if (asOne) combine.mutate(photos);
    else {
      for (const f of photos) upload.mutate(f);
      setPhotos(null);
    }
  };
  const busy = upload.isPending || combine.isPending;
  const respond = useMutation({
    mutationFn: () => api.send("POST", `/api/portal/requests/${action.id}/respond`, { responseText: answer || undefined }),
    onSuccess: () => {
      toast({ title: action.type === "document" ? "Documents submitted" : "Response sent", description: "Your firm has been notified." });
      refresh();
    },
  });
  // Count files from earlier visits too, so a returning client can finish the request.
  const canSubmit = action.type === "document" ? uploaded.length + action.documentCount > 0 : answer.trim().length > 0;

  return (
    <li className="rounded-xl border border-card-border bg-card p-4 shadow-xs">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="font-medium">{action.title}</p>
          {clientName && <p className="text-xs text-muted-foreground">For {clientName}</p>}
          {action.instructions && <p className="mt-1 text-sm text-muted-foreground">{action.instructions}</p>}
          {action.reviewNote && (
            <p className="mt-2 rounded-md border border-accent/60 bg-accent/10 px-3 py-2 text-sm">
              <span className="font-medium">Note from your firm:</span> {action.reviewNote}
            </p>
          )}
          {action.documentCount > 0 && uploaded.length === 0 && (
            <p className="mt-1 text-xs text-muted-foreground">
              {action.documentCount} file{action.documentCount > 1 ? "s" : ""} already uploaded
            </p>
          )}
        </div>
        {action.dueDate && <span className="shrink-0 text-xs text-muted-foreground">Due {formatDate(action.dueDate)}</span>}
      </div>
      <div className="mt-3 space-y-2">
        {action.type === "document" ? (
          <div className="flex flex-wrap items-center gap-2">
            <UploadButton multiple onFiles={pick} disabled={busy || !!photos} label={busy ? "Uploading…" : uploaded.length ? "Upload another file" : "Upload document"} />
            {uploaded.map((f) => (
              <span key={f} className="flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300">
                <CheckCircle2 className="h-3.5 w-3.5" /> {f}
              </span>
            ))}
          </div>
        ) : (
          <Textarea aria-label="Your answer" placeholder="Enter your response" value={answer} onChange={(e) => setAnswer(e.target.value)} rows={2} />
        )}
        {photos && (
          <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-3 text-sm">
            <p className="font-medium">{photos.length} photos selected</p>
            <label className="flex items-start gap-2">
              <input type="checkbox" className="mt-0.5 h-4 w-4" checked={asOne} onChange={(e) => setAsOne(e.target.checked)} />
              <span>These photos are pages of one document (sent as one PDF, in the order you picked them)</span>
            </label>
            {asOne && (
              <div className="space-y-1">
                <Label htmlFor={`combine-${action.id}`}>Name of the document</Label>
                <Input id={`combine-${action.id}`} value={docName} maxLength={120} onChange={(e) => setDocName(e.target.value)} />
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" disabled={busy || (asOne && !docName.trim())} onClick={sendPhotos}>
                {asOne ? "Upload as one document" : `Upload ${photos.length} files`}
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setPhotos(null)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
        <Button size="sm" disabled={!canSubmit || respond.isPending} onClick={() => respond.mutate()}>
          {action.type === "document" ? "Mark as complete" : "Submit response"}
        </Button>
      </div>
    </li>
  );
}

export function PortalHomePage({ me }: { me: Me }) {
  const { data, isLoading } = usePortalHome();
  const todos = usePortalTodos();
  if (isLoading || !data) return <Loading />;
  const nameOf = (id: string) => (data.clients.length > 1 ? data.clients.find((c) => c.id === id)?.displayName : undefined);

  return (
    <>
      <PageHeader
        title={`Welcome, ${me.user?.name.split(" ")[0]}`}
        description={
          data.actions.length + todos.count
            ? `${data.actions.length + todos.count} item${data.actions.length + todos.count > 1 ? "s require" : " requires"} your attention.`
            : "No items require your attention."
        }
      />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Section title="Your To-Do List">
          <PortalTodoCards />
          <BooksQuestionsCard />
          {data.actions.length === 0 && todos.count > 0 ? null : data.actions.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <CheckCircle2 className="h-10 w-10 text-emerald-600" />
              <p className="font-medium">No action is required at this time.</p>
              <p className="text-sm text-muted-foreground">
                {data.clients.some((c) => c.access === "full") ? "You will be notified by email when your firm requests information." : "You will be notified by email when your firm shares documents with you."}
              </p>
            </div>
          ) : (
            <ul className="space-y-3">
              {data.actions.map((a) => (
                <ActionCard key={a.id} action={a} clientName={nameOf(a.clientId)} />
              ))}
            </ul>
          )}
        </Section>
        <div className="space-y-6">
          <Section title="Your work with us">
            {data.work.length === 0 ? (
              <Empty>Nothing is in progress right now.</Empty>
            ) : (
              <ul className="space-y-4">
                {data.work.map((w) => (
                  <li key={w.id}>
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-sm font-medium">{w.period ? `${MODULE_LABELS[w.module]} · ${formatPeriod(w.period)}` : w.name}</p>
                    </div>
                    <Progress value={(w.stageNumber / Math.max(w.stageCount, 1)) * 100} aria-label={`${w.period ? `${MODULE_LABELS[w.module]} · ${formatPeriod(w.period)}` : w.name}: step ${w.stageNumber} of ${w.stageCount}`} className="my-1.5 h-2" />
                    <p className="text-sm text-muted-foreground">{w.statusLabel}</p>
                    {nameOf(w.clientId) && <p className="text-xs text-muted-foreground">{nameOf(w.clientId)}</p>}
                  </li>
                ))}
              </ul>
            )}
          </Section>
          <Section title="Services you use">
            <ul className="space-y-2 text-sm">
              {data.clients.map((c) => (
                <li key={c.id}>
                  {data.clients.length > 1 && <p className="font-medium">{c.displayName}</p>}
                  <p className="text-muted-foreground">{c.services.map((s) => MODULE_LABELS[s.module]).join(" · ") || "—"}</p>
                </li>
              ))}
            </ul>
          </Section>
          <PortalPlanningCard />
          {data.unreadThreads > 0 && (
            <Link href="/messages" className="flex items-center gap-3 rounded-xl border border-primary/40 bg-primary/5 p-4 text-sm">
              <MessageSquare className="h-5 w-5 text-primary" />
              {data.unreadThreads} unread message{data.unreadThreads > 1 ? "s" : ""} from your firm.
            </Link>
          )}
        </div>
      </div>
    </>
  );
}

export function PortalDocumentsPage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data: home } = usePortalHome();
  const { data, isLoading } = useQuery<Doc[]>({ queryKey: ["/api/portal/documents"] });
  const [clientId, setClientId] = useState<string>("");
  const target = clientId || home?.clients[0]?.id;
  const [viewing, setViewing] = useState<Doc | null>(null);
  const combine = useMutation({
    mutationFn: (v: { files: File[]; name: string; year: number | null; folder: string }) =>
      api.uploadFiles("/api/portal/documents/combine", { clientId: target, folder: v.folder, name: v.name, ...(v.year ? { year: String(v.year) } : {}) }, v.files),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/portal/documents"] });
      toast({ title: "Document uploaded", description: "Your photos were combined into one document and shared with your firm." });
    },
    onError: (e) => toast({ title: "Upload failed", description: errorMessage(e), variant: "destructive" }),
  });
  const upload = useMutation({
    mutationFn: (v: { file: File; year: number | null; folder: string }) => api.upload("/api/portal/documents", { clientId: target, folder: v.folder, ...(v.year ? { year: String(v.year) } : {}) }, v.file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/portal/documents"] });
      toast({ title: "Document uploaded", description: "The document has been shared with your firm." });
    },
    onError: (e) => toast({ title: "Upload failed", description: errorMessage(e), variant: "destructive" }),
  });
  return (
    <>
      <PageHeader
        title="Documents"
        description="Your documents by year, shared securely with your firm. All files are encrypted."
        actions={
          home && home.clients.length > 1 ? (
            <select aria-label="Whose documents" value={target} onChange={(e) => setClientId(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
              {home.clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                </option>
              ))}
            </select>
          ) : undefined
        }
      />
      {isLoading || !home ? (
        <Loading />
      ) : !target ? (
        <Empty>No documents have been shared.</Empty>
      ) : (
        <YearFolders
          key={target}
          docs={(data ?? []).filter((d) => d.clientId === target)}
          mode="portal"
          zipUrl={(y) => `/api/portal/documents/zip?clientId=${target}&year=${y}`}
          zippable={(d) => !d.locked}
          onUpload={(file, year, folder) => upload.mutate({ file, year, folder })}
          onCombine={(files, name, year, folder) => combine.mutate({ files, name, year, folder })}
          uploading={upload.isPending || combine.isPending}
          meta={(d) => docMeta(d)}
          actions={(d) =>
            d.locked ? (
              <Link href="/invoices" className="flex items-center gap-1 rounded px-2 py-1 text-xs text-muted-foreground hover:bg-muted" title="Available after the invoice is paid">
                <Lock className="h-3.5 w-3.5" /> Available after payment
              </Link>
            ) : (
              <>
                <Button size="icon" variant="ghost" aria-label={`Preview ${d.filename}`} title="Preview" onClick={() => setViewing(d)}>
                  <Eye className="h-4 w-4" />
                </Button>
                <a href={`/api/portal/documents/${d.id}/download`} className="rounded p-2 hover:bg-muted" aria-label={`Download ${d.filename}`}>
                  <Download className="h-4 w-4" />
                </a>
              </>
            )
          }
        />
      )}
      {viewing && <DocumentPreviewDialog doc={viewing} portal onClose={() => setViewing(null)} />}
    </>
  );
}

export function PortalMessagesPage({ me }: { me: Me }) {
  const qc = useQueryClient();
  const { data: home } = usePortalHome();
  const { offset, size, paged } = useServerPaged();
  const { data, isLoading } = useQuery<{ items: Array<{ id: string; subject: string; lastMessageAt: string; clientId: string; clientName: string }>; total: number }>({
    queryKey: ["/api/portal/threads", `?offset=${offset}&limit=${size}`],
    placeholderData: (prev) => prev,
  });
  const [selected, setSelected] = useState<string>();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const messageable = (home?.clients ?? []).filter((c) => c.access === "full");
  const [about, setAbout] = useState("");
  const clientId = about || messageable[0]?.id;
  const start = useMutation({
    mutationFn: () => api.send<{ id: string }>("POST", "/api/portal/threads", { clientId, subject, body }),
    onSuccess: (t) => {
      setSubject("");
      setBody("");
      setSelected(t.id);
      qc.invalidateQueries({ queryKey: ["/api/portal/threads"] });
    },
  });
  return (
    <>
      <PageHeader title="Messages" description="Secure messages with your firm." />
      {isLoading ? (
        <Loading />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
          <div className="space-y-3">
            <ThreadList threads={data?.items ?? []} selected={selected} onSelect={setSelected} showClient={messageable.length > 1} paged={paged(data?.total ?? 0)} />
            <form
              className="space-y-2 rounded-xl border border-card-border bg-card p-4"
              onSubmit={(e) => {
                e.preventDefault();
                start.mutate();
              }}
            >
              <p className="text-sm font-medium">New message</p>
              {messageable.length > 1 && (
                <select aria-label="About" value={clientId} onChange={(e) => setAbout(e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
                  {messageable.map((c) => (
                    <option key={c.id} value={c.id}>
                      About: {c.displayName}
                    </option>
                  ))}
                </select>
              )}
              <Input aria-label="Subject" placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} required />
              <Textarea aria-label="Message" placeholder="Message" value={body} onChange={(e) => setBody(e.target.value)} rows={3} required />
              <Button type="submit" size="sm" disabled={start.isPending || !clientId}>
                Send
              </Button>
            </form>
          </div>
          <div className="min-h-[360px] rounded-xl border border-card-border bg-card p-4 shadow-xs">
            {selected ? <ThreadView id={selected} portal meId={me.user?.id} /> : <NoThreadSelected />}
          </div>
        </div>
      )}
    </>
  );
}

function NameForm({ me }: { me: Me }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState(me.user?.name ?? "");
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("PATCH", "/api/auth/profile", { name }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: "Name updated" });
    },
  });
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="display-name">Full name</Label>
        <Input id="display-name" autoComplete="name" maxLength={200} value={name} onChange={(e) => setName(e.target.value)} required />
        <p className="text-xs text-muted-foreground">Displayed to your firm and used when you sign documents electronically.</p>
      </div>
      {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
      <Button type="submit" disabled={save.isPending || !name.trim() || name.trim() === me.user?.name}>
        Save name
      </Button>
    </form>
  );
}

type MyConsent = { id: string; clientId: string; purpose: string; grantedAt: string; expiresAt: string | null; revokedAt: string | null; signerName: string };

/** The consents the client has given the firm, each of which they may withdraw at any time. */
function PortalConsents() {
  const qc = useQueryClient();
  const { data: home } = usePortalHome();
  const { data, isLoading } = useQuery<MyConsent[]>({ queryKey: ["/api/portal/consents"] });
  const withdraw = useMutation({
    mutationFn: (id: string) => api.send("POST", `/api/portal/consents/${id}/revoke`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/portal/consents"] }),
  });
  const now = Date.now();
  const active = (data ?? []).filter((c) => !c.revokedAt && (!c.expiresAt || Date.parse(c.expiresAt) > now));
  const nameOf = (id: string) => ((home?.clients.length ?? 0) > 1 ? home?.clients.find((c) => c.id === id)?.displayName : undefined);
  if (isLoading) return <Loading />;
  if (!active.length) return <p className="text-sm text-muted-foreground">You have no active consents with us.</p>;
  return (
    <ul className="divide-y divide-border rounded-lg border border-border">
      {active.map((c) => (
        <li key={c.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">{CONSENT_LABELS[c.purpose] ?? c.purpose}</p>
            <p className="text-xs text-muted-foreground">
              {nameOf(c.clientId) ? `${nameOf(c.clientId)} · ` : ""}Signed by {c.signerName} on {formatDate(c.grantedAt)}
              {c.expiresAt ? ` · until ${formatDate(c.expiresAt)}` : ""}
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={withdraw.isPending}
            onClick={async () =>
              (await ask({ title: "Withdraw this consent?", description: `${CONSENT_LABELS[c.purpose] ?? "This use"} stops right away. You can sign a new consent later if you change your mind.`, confirmLabel: "Withdraw consent", destructive: true })) && withdraw.mutate(c.id)
            }
          >
            Withdraw
          </Button>
        </li>
      ))}
    </ul>
  );
}

/** Minutes after signing in that "Set a password" stays open (the server checks too). */
const SET_PASSWORD_MINUTES = 10;

function ChangePasswordForm() {
  const { toast } = useToast();
  const [currentPassword, setCurrent] = useState("");
  const [newPassword, setNew] = useState("");
  const change = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("POST", "/api/auth/password", { currentPassword, newPassword }),
    onSuccess: () => {
      setCurrent("");
      setNew("");
      toast({ title: "Password updated", description: "You have been signed out on other devices." });
    },
  });
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        change.mutate();
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="cur">Current password</Label>
        <Input id="cur" type="password" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrent(e.target.value)} required />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="new">New password</Label>
        <Input id="new" type="password" autoComplete="new-password" minLength={12} value={newPassword} onChange={(e) => setNew(e.target.value)} required />
        <p className="text-xs text-muted-foreground">At least 12 characters, including a letter and a number.</p>
      </div>
      {change.error && <p className="text-sm text-destructive">{errorMessage(change.error)}</p>}
      <Button type="submit" disabled={change.isPending}>
        Update password
      </Button>
    </form>
  );
}

/**
 * For an account made with Sign in with Google (or an emailed code), which has no password: adding one gives
 * a way in if that sign-in stops working. Only shortly after signing in, and with a fresh two-step code.
 */
function SetPasswordForm({ me }: { me: Me }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [newPassword, setNew] = useState("");
  const [confirm, setConfirm] = useState("");
  const [code, setCode] = useState("");
  const [expired, setExpired] = useState(() => !me.signedInAt || Date.now() - Date.parse(me.signedInAt) > SET_PASSWORD_MINUTES * 60_000);
  const signOut = useMutation({ mutationFn: () => api.send("POST", "/api/auth/logout"), onSuccess: () => window.location.assign("/sign-in") });
  const set = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("POST", "/api/auth/password/set", { newPassword, ...(me.user?.mfaEnabled ? { code } : {}) }),
    onSuccess: async () => {
      setNew("");
      setConfirm("");
      setCode("");
      await qc.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: "Password set", description: "You can now also sign in with your email address and this password. You have been signed out on other devices." });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "sign_in_again") setExpired(true);
    },
  });
  const mismatch = confirm.length > 0 && confirm !== newPassword;
  if (expired) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Your account signs in with Google or an emailed code and has no password. For your security, a password can be added only within {SET_PASSWORD_MINUTES} minutes of
          signing in.
        </p>
        <p className="text-sm font-medium">Sign in again to set a password.</p>
        <Button variant="outline" onClick={() => signOut.mutate()} disabled={signOut.isPending}>
          Sign in again
        </Button>
      </div>
    );
  }
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!mismatch) set.mutate();
      }}
    >
      <p className="text-sm text-muted-foreground">
        Your account signs in with Google or an emailed code and has no password. Add one so you can still sign in if that ever stops working.
      </p>
      <div className="space-y-1.5">
        <Label htmlFor="set-new">New password</Label>
        <Input id="set-new" type="password" autoComplete="new-password" minLength={12} value={newPassword} onChange={(e) => setNew(e.target.value)} required />
        <p className="text-xs text-muted-foreground">At least 12 characters, including a letter and a number.</p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="set-confirm">Confirm</Label>
        <Input id="set-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
        {mismatch && <p className="text-xs text-destructive">Passwords do not match</p>}
      </div>
      {me.user?.mfaEnabled && (
        <div className="space-y-1.5">
          <Label htmlFor="set-code">Code from your authenticator app</Label>
          <Input
            id="set-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            className="max-w-40 tabular tracking-[0.3em]"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            required
          />
        </div>
      )}
      {set.error && <p className="text-sm text-destructive">{errorMessage(set.error)}</p>}
      <Button type="submit" disabled={set.isPending || mismatch || (!!me.user?.mfaEnabled && code.length !== 6)}>
        Set password
      </Button>
    </form>
  );
}

export function ProfilePage({ me, portal = false }: { me: Me; portal?: boolean }) {
  const noPassword = me.user?.hasPassword === false;
  return (
    <>
      <PageHeader title="Account & Security" description={me.user?.email} />
      {!!me.invitations?.length && (
        <div className="mb-6 rounded-xl border border-card-border bg-card p-4">
          <Invitations me={me} />
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="Your name">
          <NameForm me={me} />
        </Section>
        <Section title="Two-step verification">
          {me.user?.mfaEnabled ? (
            <p className="flex items-center gap-2 text-sm">
              <ShieldCheck className="h-5 w-5 text-emerald-600" /> Enabled. Your account is protected with an authenticator app.
            </p>
          ) : (
            <MfaSetup embedded />
          )}
        </Section>
        {portal && (
          <Section title="Your consents" description="Permissions you've given us under IRS rules (§7216), such as AI-assisted processing. You can withdraw any of them at any time." className="lg:col-span-2">
            <PortalConsents />
          </Section>
        )}
        {noPassword ? (
          <Section title="Set a password">
            <SetPasswordForm me={me} />
          </Section>
        ) : (
          <Section title="Change password">
            <ChangePasswordForm />
          </Section>
        )}
      </div>
    </>
  );
}
